import { useMemo, useState } from "react";
import { ArrowUpRight, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, money, istToday } from "../lib/api";

const TITLE_MAP = {
  customer: "Add customer",
  supplier: "Add supplier",
  product: "Add product",
  expense: "Record expense",
  invoice: "Create invoice",
  purchase: "Create purchase order",
  payment: "Record payment",
  sales_order: "Create sales order",
  credit_note: "New credit note",
  debit_note: "New debit note",
  supplier_payment: "Record supplier payment",
  refund: "Record refund",
};

const SUBTITLE_MAP = {
  invoice: "Build a clear, GST-ready invoice from your inventory.",
  purchase: "Order stock from a supplier and keep receiving clean.",
  sales_order: "Capture a confirmed sale, then generate its invoice in one click.",
  payment: "Match a payment against an outstanding invoice.",
  customer: "Keep every customer record complete and easy to find.",
  supplier: "Add partners who keep your shelves stocked.",
  product: "Add a product or service to your catalog.",
  expense: "Keep spending visible and neatly categorised.",
  credit_note: "Issue a goodwill credit that reduces a customer's balance.",
  debit_note: "Raise a debit note that reduces what you owe a supplier.",
  supplier_payment: "Settle a purchase order and update the supplier's statement.",
  refund: "Pay back a customer's credit balance and clear it to zero.",
};

function Field({ label, children, wide = false }) {
  return <label className={`modal-field ${wide ? "wide-field" : ""}`}>{label}{children}</label>;
}

const buildInitialForm = (raw, data) => {
  const type = typeof raw === "string" ? raw : raw.type;
  const invoice = typeof raw === "object" ? raw.invoice : null;
  const edit = typeof raw === "object" && raw.edit ? raw.edit : null;
  if (type === "invoice") {
    if (edit) return { customer_id: edit.customer_id || "", tax_rate: edit.tax_rate ?? 18, tax_inclusive: edit.tax_inclusive || false, due_date: edit.due_date || istToday(14), notes: edit.notes || "", terms: edit.terms || "", lines: (edit.items || []).map((i) => ({ product_id: i.product_id || "", quantity: i.quantity, unit_price: i.unit_price })) };
    return {
      customer_id: data?.customers?.[0]?.id || "",
      tax_rate: data?.profile?.default_tax_rate ?? 18,
      tax_inclusive: false,
      due_date: istToday(14),
      notes: "",
      terms: data?.profile?.default_terms || "Payment due within 14 days.",
      lines: [{ product_id: data?.products?.[0]?.id || "", quantity: 1, unit_price: data?.products?.[0]?.price || 0 }],
    };
  }
  if (type === "sales_order") {
    if (edit) return { customer_id: edit.customer_id || "", tax_rate: edit.tax_rate ?? 18, tax_inclusive: edit.tax_inclusive || false, expected_delivery: edit.expected_delivery || istToday(7), notes: edit.notes || "", lines: (edit.items || []).map((i) => ({ product_id: i.product_id || "", quantity: i.quantity, unit_price: i.unit_price })) };
    return {
      customer_id: data?.customers?.[0]?.id || "",
      tax_rate: data?.profile?.default_tax_rate ?? 18,
      tax_inclusive: false,
      expected_delivery: istToday(7),
      notes: "",
      lines: [{ product_id: data?.products?.[0]?.id || "", quantity: 1, unit_price: data?.products?.[0]?.price || 0 }],
    };
  }
  if (type === "purchase") {
    if (edit) return { supplier_id: edit.supplier_id || "", tax_rate: edit.tax_rate ?? 18, tax_inclusive: edit.tax_inclusive || false, expected_delivery: edit.expected_delivery || istToday(7), notes: edit.notes || "", lines: (edit.items || []).map((i) => ({ product_id: i.product_id || "", quantity: i.quantity, unit_cost: i.unit_cost })) };
    return {
      supplier_id: data?.suppliers?.[0]?.id || "",
      tax_rate: data?.profile?.default_tax_rate ?? 18,
      tax_inclusive: false,
      expected_delivery: istToday(7),
      notes: "",
      lines: [{ product_id: data?.products?.[0]?.id || "", quantity: 1, unit_cost: data?.products?.[0]?.cost || 0 }],
    };
  }
  if (type === "customer") {
    if (edit) return { name: edit.name || "", company: edit.company || "", email: edit.email || "", phone: edit.phone || "", address: edit.address || "", tax_id: edit.tax_id || "" };
    return { name: "", company: "", email: "", phone: "", address: "", tax_id: "" };
  }
  if (type === "supplier") {
    if (edit) return { name: edit.name || "", company: edit.company || "", email: edit.email || "", phone: edit.phone || "", address: edit.address || "", payment_terms: edit.payment_terms || "Net 30" };
    return { name: "", company: "", email: "", phone: "", address: "", payment_terms: "Net 30" };
  }
  if (type === "product") {
    if (edit) return { name: edit.name || "", sku: edit.sku || "", category: edit.category || "General", price: edit.price ?? "", cost: edit.cost ?? "", stock: edit.stock ?? "", reorder_level: edit.reorder_level ?? "5", unit: edit.unit || "unit" };
    return { name: "", sku: "", category: "General", price: "", cost: "", stock: "", reorder_level: "5", unit: data?.profile?.default_unit || "unit" };
  }
  if (type === "expense") {
    if (edit) return { category: edit.category || "Software", description: edit.description || "", amount: edit.amount ?? "", date: edit.date || istToday(), payment_method: edit.payment_method || "Bank transfer" };
    return { category: "Software", description: "", amount: "", date: istToday(), payment_method: "Bank transfer" };
  }
  if (type === "credit_note") {
    return { customer_id: data?.customers?.[0]?.id || "", amount: "", tax_rate: 0, reason: "Goodwill adjustment" };
  }
  if (type === "debit_note") {
    const sup = (typeof raw === "object" && raw.supplier) ? raw.supplier : null;
    return { supplier_id: sup?.id || data?.suppliers?.[0]?.id || "", amount: "", tax_rate: 0, reason: "Goodwill adjustment" };
  }
  if (type === "supplier_payment") {
    const po = (typeof raw === "object" && raw.po) ? raw.po : null;
    const outstanding = po ? Math.max((po.total || 0) - (po.paid || 0), 0) : 0;
    return { purchase_order_id: po?.id || "", po_number: po?.number || "", supplier_name: po?.supplier_name || "", outstanding, amount: outstanding ? Number(outstanding.toFixed(2)) : "", method: "Bank transfer", reference: "" };
  }
  if (type === "refund") {
    const cust = (typeof raw === "object" && raw.customer) ? raw.customer : (data?.customers || []).find((c) => (c.balance || 0) < 0);
    const credit = Math.max(-(cust?.balance || 0), 0);
    return { customer_id: cust?.id || "", amount: credit ? Number(credit.toFixed(2)) : "", method: "Bank transfer", reference: "", note: "" };
  }
  if (type === "payment") {
    const target = invoice || (data?.invoices || []).find((i) => i.status !== "Paid");
    return { invoice_id: target?.id || "", amount: target ? Math.max(target.total - target.paid, 0) : "", method: "Bank transfer", reference: "" };
  }
  return {};
};

export default function CreateModal({ type: raw, data, onClose, onSaved }) {
  const type = typeof raw === "string" ? raw : raw.type;
  const editItem = typeof raw === "object" && raw.edit ? raw.edit : null;
  const [form, setForm] = useState(() => buildInitialForm(raw, data));
  const [busy, setBusy] = useState(false);

  const set = (key, value) => setForm({ ...form, [key]: value });

  const products = data?.products || [];
  const invoices = data?.invoices || [];

  const taxRates = (data?.profile?.tax_rates || []).filter((r) => r && r.label);
  const taxOptions = taxRates.length
    ? taxRates.map((r) => ({ value: String(r.rate), label: r.label }))
    : [
        { value: "0", label: "No GST" }, { value: "5", label: "GST 5%" }, { value: "12", label: "GST 12%" },
        { value: "18", label: "GST 18%" }, { value: "28", label: "GST 28%" },
      ];

  const lineTotal = useMemo(() => (form.lines || []).reduce((sum, line) => sum + Number(line.quantity || 0) * Number(line.unit_price ?? line.unit_cost ?? 0), 0), [form.lines]);
  const selectedCustomer = (data?.customers || []).find((c) => c.id === form.customer_id);
  const creditAvailable = Math.max(-(selectedCustomer?.balance || 0), 0);
  const creditNoteTotal = Number(form.amount || 0) * (1 + Number(form.tax_rate || 0) / 100);
  const taxRate = Number(form.tax_rate || 0);
  const inclusive = !!form.tax_inclusive;
  const subtotalEx = inclusive && taxRate ? lineTotal / (1 + taxRate / 100) : lineTotal;
  const gst = inclusive ? lineTotal - subtotalEx : subtotalEx * taxRate / 100;
  const grandTotal = inclusive ? lineTotal : subtotalEx + gst;

  const TaxControls = ({ prefix }) => (
    <>
      <Field label="Tax rate">
        <select data-testid={`${prefix}-gst-input`} value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)}>
          {taxOptions.map((o) => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Tax treatment">
        <select data-testid={`${prefix}-tax-mode-input`} value={inclusive ? "inclusive" : "exclusive"} onChange={(e) => set("tax_inclusive", e.target.value === "inclusive")}>
          <option value="exclusive">Exclusive (add tax on top)</option>
          <option value="inclusive">Inclusive (price includes tax)</option>
        </select>
      </Field>
    </>
  );

  const TaxSummary = () => (
    <div className="invoice-summary">
      <span>Subtotal <strong>{money(subtotalEx)}</strong></span>
      <span>{inclusive ? `Tax incl. (${form.tax_rate}%)` : `Tax (${form.tax_rate}%)`} <strong>{money(gst)}</strong></span>
      <span className="invoice-grand-total">Total <strong>{money(grandTotal)}</strong></span>
    </div>
  );

  const activeInvoice = type === "payment" ? invoices.find((invoice) => invoice.id === form.invoice_id) : null;
  const outstanding = activeInvoice ? Math.max(activeInvoice.total - activeInvoice.paid, 0) : 0;

  const updateLine = (index, key, value) => {
    setForm({ ...form, lines: form.lines.map((line, i) => (i === index ? { ...line, [key]: value } : line)) });
  };

  const selectProduct = (index, productId, priceKey) => {
    const product = products.find((entry) => entry.id === productId);
    setForm({
      ...form,
      lines: form.lines.map((line, i) =>
        i === index
          ? { ...line, product_id: productId, [priceKey]: priceKey === "unit_price" ? (product?.price || 0) : (product?.cost || 0) }
          : line
      ),
    });
  };

  const addLine = (priceKey) => {
    setForm({ ...form, lines: [...form.lines, { product_id: products[0]?.id || "", quantity: 1, [priceKey]: priceKey === "unit_price" ? (products[0]?.price || 0) : (products[0]?.cost || 0) }] });
  };

  const removeLine = (index) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== index) });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (type === "supplier_payment") {
        await api.post(`/purchase_orders/${form.purchase_order_id}/pay`, { amount: Number(form.amount), method: form.method, reference: form.reference });
        toast.success("Supplier payment recorded");
        onSaved?.({});
        onClose();
        return;
      }
      let payload = form;
      let endpoint = { customer: "customers", supplier: "suppliers", product: "products", expense: "expenses", invoice: "invoices", purchase: "purchase_orders", payment: "payments", sales_order: "sales_orders", credit_note: "credit_notes", debit_note: "debit_notes", refund: "refunds" }[type];

      if (type === "sales_order") {
        payload = {
          customer_id: form.customer_id,
          items: form.lines.map((line) => ({
            product_id: line.product_id,
            description: products.find((product) => product.id === line.product_id)?.name || "Item",
            quantity: Number(line.quantity),
            unit_price: Number(line.unit_price),
          })),
          tax_rate: Number(form.tax_rate),
          tax_inclusive: !!form.tax_inclusive,
          expected_delivery: form.expected_delivery,
          notes: form.notes,
        };
      }

      if (type === "invoice") {
        const confirmClicked = event.nativeEvent.submitter?.dataset.confirm === "true";
        payload = {
          customer_id: form.customer_id,
          items: form.lines.map((line) => ({
            product_id: line.product_id,
            description: products.find((product) => product.id === line.product_id)?.name || "Item",
            quantity: Number(line.quantity),
            unit_price: Number(line.unit_price),
          })),
          tax_rate: Number(form.tax_rate),
          tax_inclusive: !!form.tax_inclusive,
          due_date: form.due_date,
          notes: form.notes,
          terms: form.terms,
          confirm: confirmClicked,
        };
      }

      if (type === "purchase") {
        payload = {
          supplier_id: form.supplier_id,
          items: form.lines.map((line) => ({
            product_id: line.product_id,
            description: products.find((product) => product.id === line.product_id)?.name || "Item",
            quantity: Number(line.quantity),
            unit_cost: Number(line.unit_cost),
          })),
          tax_rate: Number(form.tax_rate),
          tax_inclusive: !!form.tax_inclusive,
          expected_delivery: form.expected_delivery,
          notes: form.notes,
        };
      }

      if (type === "product") {
        payload = {
          ...form,
          price: Number(form.price || 0),
          cost: Number(form.cost || 0),
          stock: Number(form.stock || 0),
          reorder_level: Number(form.reorder_level || 0),
        };
      }

      if (type === "expense" || type === "payment") {
        payload = { ...form, amount: Number(form.amount) };
      }

      if (type === "credit_note") {
        payload = { customer_id: form.customer_id, amount: Number(form.amount), tax_rate: Number(form.tax_rate || 0), reason: form.reason };
      }

      if (type === "debit_note") {
        payload = { supplier_id: form.supplier_id, amount: Number(form.amount), tax_rate: Number(form.tax_rate || 0), reason: form.reason };
      }

      if (type === "refund") {
        payload = { customer_id: form.customer_id, amount: Number(form.amount), method: form.method, reference: form.reference, note: form.note };
      }

      if (editItem && ["customer", "supplier", "product", "invoice", "sales_order", "purchase", "expense"].includes(type)) {
        const response = await api.put(`/${endpoint}/${editItem.id}`, payload);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} updated`);
        onSaved({ updated: response.data });
        return;
      }

      const response = await api.post(`/${endpoint}`, payload);
      toast.success(`${TITLE_MAP[type]} saved`);
      onSaved(type === "invoice" ? { preview: response.data } : {});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save this record");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className={`create-modal ${type === "invoice" || type === "purchase" || type === "sales_order" ? "invoice-builder-modal" : ""}`} role="dialog" aria-modal="true">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">FLOWSPHERE / {editItem ? "EDIT RECORD" : "NEW RECORD"}</p>
            <h2>{editItem ? `${type.charAt(0).toUpperCase() + type.slice(1)} details` : TITLE_MAP[type]}</h2>
            <p>{editItem ? "View and update these details." : SUBTITLE_MAP[type]}</p>
          </div>
          <button data-testid="create-modal-close-button" className="icon-button" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} data-testid={`${type}-create-form`}>
          <div className="modal-fields">
            {type === "sales_order" && (
              <>
                <Field label="Customer *">
                  <select data-testid="sales_order-customer-input" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} required>
                    {(data?.customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                  </select>
                </Field>
                <TaxControls prefix="sales_order" />
                <Field label="Expected delivery">
                  <input data-testid="sales_order-delivery-input" type="date" value={form.expected_delivery} onChange={(e) => set("expected_delivery", e.target.value)} />
                </Field>
                <Field label="Notes for customer" wide>
                  <input data-testid="sales_order-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Confirmed order, awaiting dispatch." />
                </Field>
                <div className="invoice-lines">
                  <div className="line-header">
                    <strong>Products &amp; services</strong>
                    <button data-testid="sales_order-add-line-button" type="button" className="link-button" onClick={() => addLine("unit_price")}>
                      <Plus size={14} /> Add another
                    </button>
                  </div>
                  {form.lines.map((line, index) => (
                    <div className="invoice-line" key={index}>
                      <select data-testid={`sales_order-product-input-${index}`} value={line.product_id} onChange={(e) => selectProduct(index, e.target.value, "unit_price")} required>
                        {products.map((product) => <option key={product.id} value={product.id}>{product.name} · {product.stock} in stock</option>)}
                      </select>
                      <input data-testid={`sales_order-quantity-input-${index}`} type="number" min="1" step="any" value={line.quantity} onChange={(e) => updateLine(index, "quantity", e.target.value)} />
                      <input data-testid={`sales_order-price-input-${index}`} type="number" min="0" step="any" value={line.unit_price} onChange={(e) => updateLine(index, "unit_price", e.target.value)} />
                      {form.lines.length > 1 && (
                        <button data-testid={`sales_order-remove-line-button-${index}`} type="button" className="icon-button danger-icon" onClick={() => removeLine(index)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <TaxSummary />
              </>
            )}
            {type === "invoice" && (
              <>
                <Field label="Customer *">
                  <select data-testid="invoice-customer-input" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} required>
                    {(data?.customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                  </select>
                </Field>
                <TaxControls prefix="invoice" />
                <Field label="Due date">
                  <input data-testid="invoice-due-date-input" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
                </Field>
                <Field label="Notes for customer" wide>
                  <input data-testid="invoice-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Thank you for your continued trust." />
                </Field>
                <div className="invoice-lines">
                  <div className="line-header">
                    <strong>Products &amp; services</strong>
                    <button data-testid="invoice-add-line-button" type="button" className="link-button" onClick={() => addLine("unit_price")}>
                      <Plus size={14} /> Add another
                    </button>
                  </div>
                  {form.lines.map((line, index) => (
                    <div className="invoice-line" key={index}>
                      <select data-testid={`invoice-product-input-${index}`} value={line.product_id} onChange={(e) => selectProduct(index, e.target.value, "unit_price")} required>
                        {products.map((product) => <option key={product.id} value={product.id}>{product.name} · {product.stock} in stock</option>)}
                      </select>
                      <input data-testid={`invoice-quantity-input-${index}`} type="number" min="1" step="any" value={line.quantity} onChange={(e) => updateLine(index, "quantity", e.target.value)} />
                      <input data-testid={`invoice-price-input-${index}`} type="number" min="0" step="any" value={line.unit_price} onChange={(e) => updateLine(index, "unit_price", e.target.value)} />
                      {form.lines.length > 1 && (
                        <button data-testid={`invoice-remove-line-button-${index}`} type="button" className="icon-button danger-icon" onClick={() => removeLine(index)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <TaxSummary />
              </>
            )}
            {type === "purchase" && (
              <>
                <Field label="Supplier *">
                  <select data-testid="purchase-supplier-input" value={form.supplier_id} onChange={(e) => set("supplier_id", e.target.value)} required>
                    {(data?.suppliers || []).map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
                  </select>
                </Field>
                <TaxControls prefix="purchase" />
                <Field label="Expected delivery">
                  <input data-testid="purchase-delivery-input" type="date" value={form.expected_delivery} onChange={(e) => set("expected_delivery", e.target.value)} />
                </Field>
                <Field label="Notes" wide>
                  <input data-testid="purchase-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Delivery to main warehouse" />
                </Field>
                <div className="invoice-lines">
                  <div className="line-header">
                    <strong>Products to order</strong>
                    <button data-testid="purchase-add-line-button" type="button" className="link-button" onClick={() => addLine("unit_cost")}>
                      <Plus size={14} /> Add another
                    </button>
                  </div>
                  {form.lines.map((line, index) => (
                    <div className="invoice-line" key={index}>
                      <select data-testid={`purchase-product-input-${index}`} value={line.product_id} onChange={(e) => selectProduct(index, e.target.value, "unit_cost")} required>
                        {products.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
                      </select>
                      <input data-testid={`purchase-quantity-input-${index}`} type="number" min="1" value={line.quantity} onChange={(e) => updateLine(index, "quantity", e.target.value)} />
                      <input data-testid={`purchase-cost-input-${index}`} type="number" min="0" value={line.unit_cost} onChange={(e) => updateLine(index, "unit_cost", e.target.value)} />
                      {form.lines.length > 1 && (
                        <button data-testid={`purchase-remove-line-button-${index}`} type="button" className="icon-button danger-icon" onClick={() => removeLine(index)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <TaxSummary />
              </>
            )}
            {type === "customer" && (
              <>
                <Field label="Customer name *"><input data-testid="customer-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Maya Foods" required /></Field>
                <Field label="Company"><input data-testid="customer-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
                <Field label="Email"><input data-testid="customer-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
                <Field label="Phone"><input data-testid="customer-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Address" wide><input data-testid="customer-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
                <Field label="Tax ID"><input data-testid="customer-tax-input" value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} /></Field>
                {editItem && (
                  <Field label="Outstanding balance (read-only)" wide>
                    <input data-testid="customer-balance-readonly" value={money(editItem.balance || 0)} readOnly disabled />
                  </Field>
                )}
              </>
            )}
            {type === "supplier" && (
              <>
                <Field label="Supplier name *"><input data-testid="supplier-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required /></Field>
                <Field label="Company"><input data-testid="supplier-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
                <Field label="Email"><input data-testid="supplier-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
                <Field label="Phone"><input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Address" wide><input data-testid="supplier-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
                <Field label="Payment terms">
                  <select data-testid="supplier-terms-input" value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)}>
                    <option>Net 15</option><option>Net 30</option><option>Net 45</option><option>Advance</option>
                  </select>
                </Field>
              </>
            )}
            {type === "product" && (
              <>
                <Field label="Product name *"><input data-testid="product-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required /></Field>
                <Field label="SKU"><input data-testid="product-sku-input" value={form.sku} onChange={(e) => set("sku", e.target.value)} /></Field>
                <Field label="Category"><input data-testid="product-category-input" value={form.category} onChange={(e) => set("category", e.target.value)} /></Field>
                <Field label="Unit">
                  <select data-testid="product-unit-input" value={form.unit} onChange={(e) => set("unit", e.target.value)}>
                    <option>unit</option><option>piece</option><option>box</option><option>pack</option><option value="kg">kg (kilogram)</option><option value="gram">gram</option><option value="litre">litre</option><option>month</option><option>hour</option>
                  </select>
                </Field>
                <Field label="Selling price *"><input data-testid="product-price-input" type="number" min="0" step="any" value={form.price} onChange={(e) => set("price", e.target.value)} required /></Field>
                <Field label="Purchase cost"><input data-testid="product-cost-input" type="number" min="0" step="any" value={form.cost} onChange={(e) => set("cost", e.target.value)} /></Field>
                <Field label="Opening stock *"><input data-testid="product-stock-input" type="number" min="0" step="any" value={form.stock} onChange={(e) => set("stock", e.target.value)} required /></Field>
                <Field label="Reorder level"><input data-testid="product-reorder-input" type="number" min="0" value={form.reorder_level} onChange={(e) => set("reorder_level", e.target.value)} /></Field>
              </>
            )}
            {type === "expense" && (
              <>
                <Field label="Category">
                  <select data-testid="expense-category-input" value={form.category} onChange={(e) => set("category", e.target.value)}>
                    <option>Software</option><option>Rent</option><option>Utilities</option><option>Marketing</option><option>Travel</option><option>Salaries</option><option>Office supplies</option><option>Other</option>
                  </select>
                </Field>
                <Field label="Description *"><input data-testid="expense-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} required /></Field>
                <Field label="Amount *"><input data-testid="expense-amount-input" type="number" min="1" step="any" value={form.amount} onChange={(e) => set("amount", e.target.value)} required /></Field>
                <Field label="Date"><input data-testid="expense-date-input" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
                <Field label="Payment method">
                  <select data-testid="expense-payment-input" value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
                    <option>Bank transfer</option><option>Cash</option><option>Credit card</option><option>UPI</option><option>Cheque</option>
                  </select>
                </Field>
              </>
            )}
            {type === "payment" && (
              <>
                <Field label="Invoice *">
                  <select data-testid="payment-invoice-input" value={form.invoice_id} onChange={(e) => { set("invoice_id", e.target.value); const target = invoices.find((invoice) => invoice.id === e.target.value); setForm((prev) => ({ ...prev, invoice_id: e.target.value, amount: target ? Math.max(target.total - target.paid, 0) : "" })); }} required>
                    {invoices.filter((invoice) => invoice.status !== "Paid").map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>{invoice.number} · {invoice.customer_name} · {money(invoice.total - invoice.paid)} due</option>
                    ))}
                  </select>
                </Field>
                <Field label="Payment method">
                  <select data-testid="payment-method-input" value={form.method} onChange={(e) => set("method", e.target.value)}>
                    <option>Bank transfer</option><option>Cash</option><option>Credit card</option><option>UPI</option><option>Cheque</option>
                  </select>
                </Field>
                <Field label="Amount *"><input data-testid="payment-amount-input" type="number" min="1" step="any" max={outstanding || undefined} value={form.amount} onChange={(e) => set("amount", e.target.value)} required /></Field>
                <Field label="Reference"><input data-testid="payment-reference-input" value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="UTR / cheque no." /></Field>
                {activeInvoice && (
                  <div className="payment-summary" data-testid="payment-summary">
                    <div><span>Invoice total</span><strong>{money(activeInvoice.total)}</strong></div>
                    <div><span>Already paid</span><strong>{money(activeInvoice.paid)}</strong></div>
                    <div className="highlight"><span>Outstanding</span><strong>{money(outstanding)}</strong></div>
                    <button type="button" className="link-button" data-testid="payment-fill-full" onClick={() => set("amount", outstanding)}>Fill full balance</button>
                  </div>
                )}
              </>
            )}
            {type === "credit_note" && (
              <>
                <Field label="Customer *">
                  <select data-testid="credit_note-customer-input" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} required>
                    {(data?.customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                  </select>
                </Field>
                <Field label="Credit amount *"><input data-testid="credit_note-amount-input" type="number" min="1" step="any" value={form.amount} onChange={(e) => set("amount", e.target.value)} required /></Field>
                <Field label="Tax rate">
                  <select data-testid="credit_note-gst-input" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)}>
                    {taxOptions.map((o) => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Reason" wide>
                  <input data-testid="credit_note-reason-input" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Goodwill adjustment, damaged goods, etc." />
                </Field>
                <div className="payment-summary" data-testid="credit_note-summary">
                  <div><span>Credit subtotal</span><strong>{money(Number(form.amount || 0))}</strong></div>
                  <div><span>Tax ({form.tax_rate || 0}%)</span><strong>{money(Number(form.amount || 0) * Number(form.tax_rate || 0) / 100)}</strong></div>
                  <div className="highlight"><span>Total credit</span><strong>{money(creditNoteTotal)}</strong></div>
                </div>
              </>
            )}
            {type === "debit_note" && (
              <>
                <Field label="Supplier *">
                  <select data-testid="debit_note-supplier-input" value={form.supplier_id} onChange={(e) => set("supplier_id", e.target.value)} required>
                    {(data?.suppliers || []).map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
                  </select>
                </Field>
                <Field label="Debit amount *"><input data-testid="debit_note-amount-input" type="number" min="1" step="any" value={form.amount} onChange={(e) => set("amount", e.target.value)} required /></Field>
                <Field label="Tax rate">
                  <select data-testid="debit_note-gst-input" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)}>
                    {taxOptions.map((o) => <option key={o.value + o.label} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Reason" wide>
                  <input data-testid="debit_note-reason-input" value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Goodwill adjustment, damaged goods, etc." />
                </Field>
                <div className="payment-summary" data-testid="debit_note-summary">
                  <div><span>Debit subtotal</span><strong>{money(Number(form.amount || 0))}</strong></div>
                  <div><span>Tax ({form.tax_rate || 0}%)</span><strong>{money(Number(form.amount || 0) * Number(form.tax_rate || 0) / 100)}</strong></div>
                  <div className="highlight"><span>Total debit</span><strong>{money(creditNoteTotal)}</strong></div>
                </div>
              </>
            )}
            {type === "supplier_payment" && (
              <>
                <Field label="Purchase order"><input data-testid="sp-po-input" value={form.po_number} disabled /></Field>
                <Field label="Supplier"><input data-testid="sp-supplier-input" value={form.supplier_name} disabled /></Field>
                <Field label="Payment method">
                  <select data-testid="sp-method-input" value={form.method} onChange={(e) => set("method", e.target.value)}>
                    <option>Bank transfer</option><option>Cash</option><option>UPI</option><option>Credit card</option><option>Cheque</option>
                  </select>
                </Field>
                <Field label="Amount *"><input data-testid="sp-amount-input" type="number" min="1" step="any" max={form.outstanding || undefined} value={form.amount} onChange={(e) => set("amount", e.target.value)} required /></Field>
                <Field label="Reference" wide><input data-testid="sp-reference-input" value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="UTR / cheque no." /></Field>
                <div className="payment-summary" data-testid="sp-summary">
                  <div><span>Outstanding</span><strong>{money(form.outstanding)}</strong></div>
                  <div className="highlight"><span>Balance after payment</span><strong>{money(Math.max((form.outstanding || 0) - Number(form.amount || 0), 0))}</strong></div>
                </div>
              </>
            )}
            {type === "refund" && (
              <>
                <Field label="Customer *">
                  <select data-testid="refund-customer-input" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} required>
                    {(data?.customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                  </select>
                </Field>
                <Field label="Refund method">
                  <select data-testid="refund-method-input" value={form.method} onChange={(e) => set("method", e.target.value)}>
                    <option>Bank transfer</option><option>Cash</option><option>Credit card</option><option>UPI</option><option>Cheque</option>
                  </select>
                </Field>
                <Field label="Refund amount *"><input data-testid="refund-amount-input" type="number" min="1" step="any" max={creditAvailable || undefined} value={form.amount} onChange={(e) => set("amount", e.target.value)} required /></Field>
                <Field label="Reference"><input data-testid="refund-reference-input" value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="UTR / cheque no." /></Field>
                <Field label="Note" wide><input data-testid="refund-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Refund against returned order" /></Field>
                <div className="payment-summary" data-testid="refund-summary">
                  <div><span>Credit available</span><strong>{money(creditAvailable)}</strong></div>
                  <div className="highlight"><span>Balance after refund</span><strong>{money(creditAvailable - Number(form.amount || 0))}</strong></div>
                  {creditAvailable > 0 && <button type="button" className="link-button" data-testid="refund-fill-full" onClick={() => set("amount", Number(creditAvailable.toFixed(2)))}>Refund full credit</button>}
                </div>
              </>
            )}
          </div>
          <div className="modal-actions">
            <button data-testid="create-modal-cancel-button" type="button" className="secondary-button" onClick={onClose}>Cancel</button>
            {type === "invoice" && (
              <button data-testid="invoice-confirm-button" data-confirm="true" className="secondary-button" disabled={busy}>Save &amp; confirm</button>
            )}
            <button data-testid={`${type}-save-button`} className="primary-button" disabled={busy}>
              {busy ? "Saving…" : editItem ? `Update ${type.replace("_", " ")}` : ({ sales_order: "Save sales order", credit_note: "Issue credit note", debit_note: "Issue debit note", supplier_payment: "Record payment", refund: "Record refund" }[type] || `Save ${type}`)}<ArrowUpRight size={15} />
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
